// Shared page components
import PageNavigation from "@components/navigation/pageNavigation";
import PageHeader from "@components/pageHeader";
import PageFooter from "@components/navigation/pageFooter";

// Translations
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

// Next components
import Image from "next/image";
import Link from "next/link";

// SEO
import { NextSeo } from "next-seo";

// Icons
import { ArrowNarrowRightIcon } from "@heroicons/react/solid";

// Server runtime configuration
import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();

const Projects = ({ projects }) => {
  const { t } = useTranslation(["common", "projects"]);

  return (
    <>
      {/* SEO */}
      <NextSeo
        title={t("projects:seo.title")}
        description={t("projects:seo.description")}
      />
      {/* Page content */}
      <PageNavigation />
      <PageHeader
        title={t("projects:header.title")}
        description={t("projects:header.description")}
      />
      <main className="grid grid-cols-1 gap-6 text-black c-container sm:grid-cols-2 lg:grid-cols-3">
        {/* Projects */}
        {projects.data.map((project, index) => {
          const { name, shortDescription, year, media } = project.attributes;
          return (
            <Link href={`/project/${project.id}`} key={`project-${index}`}>
              <a className="flex flex-col justify-between transition rounded shadow hover:bg-default group hover:text-white dark:text-white bg-neutral-100 dark:bg-neutral-800">
                <div>
                  <Image
                    src={`${publicRuntimeConfig.baseUrl}${media?.data[0]?.attributes.url}`}
                    height={media.data[0].attributes.height}
                    width={media.data[0].attributes.width}
                    alt={media.data[0].attributes.alternativeText}
                    className="rounded-t aspect-[4/3]"
                  />
                  <article className="p-6">
                    <span className="text-sm font-semibold tracking-wide transition text-default group-hover:text-white">
                      {year}&nbsp;
                    </span>
                    <h1 className="text-lg font-semibold">{name}</h1>
                    <p className="mt-2 text-sm">{shortDescription}</p>
                  </article>
                </div>
                <aside className="flex items-end p-6">
                  <p className="inline-block items-end !mt-auto !text-base text-center button border-default text-default before:bg-default dark:text-white dark:border-white dark:before:bg-white group-hover:bg-white group-hover:text-default group-hover:before:before:right-0 group-hover:before:opacity-0">
                    {t("showMore")}
                  </p>
                </aside>
              </a>
            </Link>
          );
        })}
      </main>
      <PageFooter />
    </>
  );
};

export default Projects;

export async function getStaticProps({ locale }) {
  const projectsResponse = await fetch(
    `${publicRuntimeConfig.baseUrl}/api/projects?locale=${locale}&populate=media`,
    {
      headers: {
        Authorization: `Bearer ${process.env.STRAPI_KEY}`,
      },
    }
  );

  const projects = await projectsResponse.json();

  return {
    props: {
      projects,
      ...(await serverSideTranslations(locale, ["common", "projects"])),
    },
    revalidate: 60,
  };
}
